import { useState, useCallback, useRef, useEffect } from 'react';
import { useConversation } from '@elevenlabs/react';
import { saveKrishnaAiConv } from '../../../apis/auth';

// Utility functions for message processing
const extractMessageContent = (message) => {
  return message?.message || message?.content || message?.text || message?.response || '';
};

const getMessageType = (message) => {
  if (message?.type === 'user_transcript' || message?.source === 'user') {
    return 'user';
  }
  if (message?.type === 'agent_response' || message?.source === 'ai' || message?.source === 'agent') {
    return 'agent';
  }
  return 'agent';
};

const isEmptyMessage = (content) => {
  return !content || content.trim() === '';
};

/**
 * Custom hook for managing ElevenLabs chat conversations
 * Handles connection, messaging, and conversation state
 */
export const useElevenLabsChat = (onConnectionLoss) => {
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [micMuted, setMicMuted] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [debouncedIsSpeaking, setDebouncedIsSpeaking] = useState(false);
  const [isReadyForAnimation, setIsReadyForAnimation] = useState(false);
  const [interactionMode, setInteractionMode] = useState("voice");
  const [chatHistory, setChatHistory] = useState([]);
  const [allowTyping, setAllowTyping] = useState(false);
  const [waitingForAgent, setWaitingForAgent] = useState(false);



  const isConnectingRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const wasConnectedRef = useRef(false);
  const hasSetAnimationReadyRef = useRef(false);

  // Update chat history
  const handleUpdateChatHistory = useCallback((type, content) => {
    const obj = { type, content, timestamp: new Date() };
    setChatHistory(prev => [...prev, obj]);
  }, []);

  const pushUserMessage = useCallback((content) => {
    if (!content || !content.trim()) return;

    handleUpdateChatHistory("user", content.trim());
    setWaitingForAgent(true);
  }, [handleUpdateChatHistory]);


  // Handle incoming messages for chat history
  const handleMessageForHistory = useCallback((message) => {

    const content = extractMessageContent(message);
    if (isEmptyMessage(content)) return;

    const messageType = getMessageType(message);

    handleUpdateChatHistory(messageType, content.trim());
    if (messageType === "agent") {
      setWaitingForAgent(false);
    }
    setAllowTyping(messageType === 'user');
  }, [handleUpdateChatHistory]);

  // Clear chat history
  const clearChatHistory = useCallback(() => {
    setChatHistory([]);
    setAllowTyping(false);
  }, []);

  const handleCaptureKrishnaAiData = useCallback(async () => {
    const convId = localStorage.getItem("elevenLabsConvId");
    if (!convId) return;

    try {
      setIsFinalizing(true);

      const res = await saveKrishnaAiConv(convId, "completed");

      const results = res?.data?.data?.data_collection_results;

      if (results) {
        setUserDetails(results);
      }

      localStorage.removeItem("elevenLabsConvId");

    } catch (err) {
    } finally {
      setIsFinalizing(false);
    }
  }, []);

  // Initialize ElevenLabs conversation
  const conversation = useConversation({
    agentId: import.meta.env.VITE_ELEVENLABS_AGENT_ID,
    micMuted,
    onConnect: () => {
      console.log('[ElevenLabs SDK] Connected successfully', {
        timestamp: new Date().toISOString(),
        micMuted,
      });
      setIsConnected(true);
      setIsConnecting(false);
      setConnectionError(null);
      // setMicMuted(false);
      isConnectingRef.current = false;
      wasConnectedRef.current = true;
    },
    onDisconnect: () => {
      console.log('[ElevenLabs SDK] Disconnected', {
        timestamp: new Date().toISOString(),
        wasConnected: wasConnectedRef.current,
      });
      setIsConnected(false);
      setIsConnecting(false);
      setMicMuted(true); // Mute mic when disconnected
      setIsReadyForAnimation(false); // Reset animation ready state
      hasSetAnimationReadyRef.current = false; // Reset tracking
      // Always navigate to step 3 (form) on any disconnect
      if (wasConnectedRef.current) {
        handleCaptureKrishnaAiData();
        onConnectionLoss?.();
      }
      wasConnectedRef.current = false;
      clearChatHistory(); // Clear chat history on disconnect
    },
    onMessage: (message) => {
      handleMessageForHistory(message);
      // Track first agent response - only set once
      if (!hasSetAnimationReadyRef.current) {
        const agentSources = ['agent_response', 'ai', 'agent'];
        if (agentSources.includes(message?.type) || agentSources.includes(message?.source)) {
          hasSetAnimationReadyRef.current = true;
          setTimeout(() => {
            setIsReadyForAnimation(true);
          }, 500);
        }
      }
    },
    onError: (error) => {
      console.error('[ElevenLabs SDK] Error occurred', {
        timestamp: new Date().toISOString(),
        error: error.message,
        fullError: error,
      });
      setConnectionError(error.message);
      setIsConnecting(false);
      isConnectingRef.current = false;
    },
    volume: interactionMode == "voice" ? 1 : 0
  });

  // Debounce isSpeaking state to prevent UI thrashing (check every 500ms)
  useEffect(() => {
    if (!isConnected) {
      if (debouncedIsSpeaking) {
        setDebouncedIsSpeaking(false);
      }
      return;
    }

    const checkSpeakingState = () => {
      const currentIsSpeaking = conversation?.isSpeaking === true;

      if (currentIsSpeaking !== debouncedIsSpeaking) {
        setDebouncedIsSpeaking(currentIsSpeaking);
      }

    };

    debounceTimeoutRef.current = setInterval(checkSpeakingState, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearInterval(debounceTimeoutRef.current);
      }
    };
  }, [isConnected, conversation?.isSpeaking, debouncedIsSpeaking]);

  useEffect(() => {
    if (!isConnected) {
      setMicMuted(true);
      return;
    }
    if (interactionMode === "text") {
      setMicMuted(true);
    } else {
      setMicMuted(false);
    }
  }, [interactionMode, isConnected]);



  // Connect to ElevenLabs agent
  const handleAutoConnect = useCallback(async (type = 'public') => {
    if (isConnectingRef.current) return;

    try {
      isConnectingRef.current = true;
      setIsConnecting(true);
      setConnectionError(null);

      // Request microphone permission
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch (err) {
        // Continue - connection will still be established
      }

      // Start the session
      const convId = await conversation.startSession({
        agentId: import.meta.env.VITE_ELEVENLABS_AGENT_ID
      });

      // Store conversation ID in localStorage and call API
      if (convId) {
        localStorage.setItem('elevenLabsConvId', convId);
        setConversationId(convId);
        // Call API to create conversation entry with 'in-progress' status
        try {
          await saveKrishnaAiConv(convId, 'in-progress');
        } catch (error) {
        }
      }

      isConnectingRef.current = false;
      setIsConnecting(false);
    } catch (error) {
      setConnectionError(error.message || 'Failed to connect. Please try again.');
      isConnectingRef.current = false;
      setIsConnecting(false);
    }
  }, [conversation]);

  // Disconnect from ElevenLabs agent
  const handleDisconnect = useCallback(async () => {
    try {
      await conversation.endSession();
      // API call happens automatically in onDisconnect callback
      setConversationId(null);
      setIsOpen(false);
    } catch (error) {
    }
  }, [conversation]);

  // Toggle microphone mute
  const handleToggleMute = useCallback(() => {
    setMicMuted(prev => !prev);
  }, []);

  // Open chat panel and auto-connect
  const handleOpenChat = useCallback(() => {
    setIsOpen(true);
    handleAutoConnect('public');
  }, [handleAutoConnect]);

  // Handle button click - opens/closes chat
  const handleButtonClick = useCallback(() => {
    if (isOpen) {
      handleDisconnect();
      setIsOpen(false);
    } else {
      handleOpenChat();
    }
  }, [isOpen, handleDisconnect, handleOpenChat]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearInterval(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    isOpen, setIsOpen, conversationId, isConnected, isConnecting,
    connectionError, micMuted, setMicMuted,
    conversation,
    isSpeaking: conversation.isSpeaking,
    isListening: conversation.isListening,
    debouncedIsSpeaking,
    isReadyForAnimation,
    handleButtonClick, handleToggleMute, handleOpenChat, handleDisconnect,
    handleCaptureKrishnaAiData, userDetails, isFinalizing,
    interactionMode, setInteractionMode,
    chatHistory, allowTyping, clearChatHistory,
    pushUserMessage,
    waitingForAgent,
  };
};
